/**
 * Leadership bio drawer.
 *
 * Bios render into hidden panels in the markup; this only reveals one at a
 * time. The "Read more" affordance stays hidden until the ready attribute is
 * set, so a dead bundle leaves static profile cards rather than inert buttons.
 *
 * Adds focus trapping and focus restore, which the reference build's version
 * does not do.
 */
import { setScrollLock } from './scroll-lock.js';

const FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

export function initLeadership() {
    document.querySelectorAll('[data-awdd-leadership]').forEach(root => {
        const drawer = root.querySelector('[data-awdd-bio-drawer]');
        if (!drawer) return;

        // Portal the drawer to <body>. header.php gives #content
        // `relative z-10`, which opens a local stacking context; the drawer
        // was nested inside it (#content > section > .container-wide >
        // [data-awdd-leadership] > .awdd-drawer), so its `z-index: 70`
        // (resources/css/app.css) only ever ranks against siblings *inside*
        // #content -- it never gets to compete with the site header, a
        // sibling of #content at `fixed` `z-50`. What actually competes
        // with the header from outside is #content itself at z-10, which
        // loses. Moving the drawer to be a direct child of <body> is the
        // same fix header.php already applies to the mobile nav drawer
        // (see template-parts/header/drawer.php's docblock) -- escape the
        // trapping ancestor rather than chase it with a higher z-index,
        // which cannot work from inside the trap.
        //
        // Trade-off: once moved, the drawer is no longer a descendant of
        // its block wrapper, so a `selector` rule typed into this block's
        // Styles-tab Additional CSS (which targets .awdd-block-{hash}) can
        // no longer match it. Acceptable for a drawer that must render
        // above the header either way.
        document.body.appendChild(drawer);

        const panel = drawer.querySelector('.awdd-drawer__panel');
        const panels = Array.from(drawer.querySelectorAll('[data-awdd-bio-panel]'));
        const openers = Array.from(root.querySelectorAll('[data-awdd-bio-open]'));
        let lastFocused = null;

        const close = () => {
            drawer.hidden = true;
            drawer.removeAttribute('data-open');
            setScrollLock(drawer, false);
            panels.forEach(p => { p.hidden = true; });
            if (lastFocused) lastFocused.focus();
            lastFocused = null;
        };

        const open = index => {
            lastFocused = document.activeElement;
            panels.forEach(p => {
                p.hidden = p.getAttribute('data-awdd-bio-panel') !== String(index);
            });
            drawer.hidden = false;
            // Next frame, so the transition runs from the closed position.
            requestAnimationFrame(() => drawer.setAttribute('data-open', ''));
            setScrollLock(drawer, true);
            const first = panel.querySelector(FOCUSABLE);
            if (first) first.focus();
        };

        openers.forEach(btn => {
            btn.addEventListener('click', () => open(btn.getAttribute('data-awdd-bio-open')));
        });

        drawer.querySelectorAll('[data-awdd-bio-close]').forEach(el => {
            el.addEventListener('click', close);
        });

        document.addEventListener('keydown', e => {
            if (drawer.hidden) return;

            if (e.key === 'Escape') {
                close();
                return;
            }

            if (e.key !== 'Tab') return;

            const items = Array.from(panel.querySelectorAll(FOCUSABLE)).filter(el => el.offsetParent !== null);
            if (!items.length) return;

            const first = items[0];
            const last = items[items.length - 1];

            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        });

        root.setAttribute('data-awdd-leadership-ready', '');
    });
}
